/* eslint-disable react/no-unstable-nested-components */
import type { SelectionEvent } from "@viselect/react";
import SelectionArea from "@viselect/react";
import type { IndicatorSelection, SheetIndicator } from "components/financials/modern/types";
import classNames from "components/ui/classNames";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import sortArray from "lib/utils/sortArray";
import { noop } from "lodash/fp";
import { useCallback, useId, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

const DATA_SELECTABLE = "data-selectable";

type SerializedSelectableShape = {
  reportDate: string;
  index: number;
  indicator: string;
};

function getSelectable(row: HasReportDate, index: number, indicator: SheetIndicator) {
  return JSON.stringify({
    reportDate: row.reportDate,
    index,
    indicator: indicator.objectId,
  });
}

function extractSelectables(elements: Element[]) {
  return elements
    .map((node) => node.getAttribute(DATA_SELECTABLE))
    .filter(Boolean)
    .map(String);
}

type SelectionFn<ReturnType> = (row: HasReportDate, index: number, indicator: SheetIndicator) => ReturnType;

type SelectableAreaState = {
  getSelectable: SelectionFn<string>;
  getSelectableClassNames: SelectionFn<string>;
  isNodeSelected: SelectionFn<boolean>;
  indicator: SheetIndicator | null;
  selection: IndicatorSelection[];
};

interface SelectableAreaProps {
  indicators: SheetIndicator[];
  children: (state: SelectableAreaState) => any;
  sortDirection: "ASC" | "DESC";
}

type HasReportDate = {
  reportDate: Date;
};

export default function SelectableArea({ indicators, children, sortDirection }: SelectableAreaProps) {
  const rootId = useId();
  const hasPointer = useMediaQuery("(pointer: fine)");
  const [selectedRowsAndColumns, setSelectedRowsAndColumns] = useState<Set<string>>(() => new Set());

  function isNodeSelected(row: HasReportDate, index: number, indicator: SheetIndicator) {
    return selectedRowsAndColumns.has(getSelectable(row, index, indicator));
  }

  function getSelectableClassNames(row: HasReportDate, index: number, indicator: SheetIndicator) {
    return classNames("selectable select-none", {
      "bg-teal-100/40 text-teal-700 dark:bg-sky-600/20 dark:text-white": isNodeSelected(row, index, indicator),
    });
  }

  const onStart = useCallback(({ selection }: SelectionEvent) => {
    selection.clearSelection();
    setSelectedRowsAndColumns(() => new Set());
  }, []);

  const onMoveSelection = useCallback(({ selection, store }: SelectionEvent) => {
    const hasCrossSelections = new Set(
      [...extractSelectables(store.selected).values()].map((key) => JSON.parse(key).indicator),
    );
    if (hasCrossSelections.size > 1) {
      selection.cancel();
      setSelectedRowsAndColumns(() => new Set());
      return;
    }

    const { added, removed } = store.changed;
    if (added.length === 0 && removed.length === 0) {
      return;
    }

    setSelectedRowsAndColumns((previous) => {
      const next = new Set(previous);
      extractSelectables(added).forEach((id) => next.add(id));
      extractSelectables(removed).forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const onStopSelection = useCallback(({ store }: SelectionEvent) => {
    const nodes = store.selected;
    const selected = extractSelectables(nodes);

    // If there's just one selected, then there's nothing to sum
    if (selected.length <= 1) {
      // Do not call `.clearSelection()` here, as it will cause a re-render/infinite loop
      setSelectedRowsAndColumns(() => new Set());
    }
  }, []);

  const rawSelections = [...selectedRowsAndColumns].map((selectable) =>
    JSON.parse(selectable),
  ) as SerializedSelectableShape[];
  const indicator =
    indicators.find(({ objectId }) => {
      const selected = rawSelections.length > 0 ? rawSelections[0].indicator : null;
      return objectId === selected;
    }) || null;
  const series = sortArray(indicator?.series || [], "DESC", sortDirection);

  const selection = rawSelections
    .sort((a, b) => {
      return compareAsc(parseISO(a.reportDate), parseISO(b.reportDate));
    })
    .map(({ reportDate, index }) => {
      if (!indicator) {
        return null;
      }

      return {
        rowIndex: index,
        reportDate: parseISO(reportDate),
        value: series[index],
      };
    })
    .filter(notUndefinedOrNull);

  return (
    <div
      id={rootId}
      className={classNames({
        "overflow-auto": !hasPointer,
      })}
    >
      <SelectionArea
        onStart={hasPointer ? onStart : noop}
        onMove={hasPointer ? onMoveSelection : noop}
        onStop={hasPointer ? onStopSelection : noop}
        selectables={`[id="${rootId}"] .selectable`}
        features={{
          touch: hasPointer,
          range: hasPointer,
          singleTap: {
            allow: hasPointer,
            intersect: "native",
          },
        }}
        behaviour={{
          overlap: "drop",
          intersect: "touch",
          startThreshold: 10,
          triggers: [0],
          scrolling: {
            speedDivider: 10,
            manualSpeed: 750,
            startScrollMargins: {
              x: 0,
              y: 0,
            },
          },
        }}
      >
        {children({
          getSelectable,
          getSelectableClassNames,
          isNodeSelected,
          indicator,
          selection,
        })}
      </SelectionArea>
    </div>
  );
}
